import React from "react";
import classNames from "classnames";
import { ContextMenuTrigger } from "react-contextmenu";
import { useDrop } from "react-dnd";
import tinycolor from "tinycolor2";

import Event, { DraggableType } from "../shared/Event";

import styles from "./WeekView.less";

const contextMenuCollect = (props) => ({
  eventId: props.eventId,
  jobId: props.jobId ? props.jobId : 0,
  date: props.date,
});

const getContextMenuIdForEvent = (e) => {
  if (e.bid) {
    return "BID_MENU";
  } else if (e.purchaseOrder || e.inventoryPurchaseOrder) {
    return "DELIVERY_MENU";
  } else if (e.job) {
    return "JOB_MENU";
  } else {
    return "NONE";
  }
};

const makeEventStyle = ({ color }) => {
  return !color
    ? null
    : {
        backgroundColor: color,
        borderColor: tinycolor(color).darken(40),
        color: tinycolor(color).getBrightness() < 150 ? "#fff" : "#000",
      };
};

const Day = ({
  active,
  crew,
  date,
  settings,
  mode,
  events,
  onReschedule,
  onScheduleBid,
  onScheduleDelivery,
  onScheduleJob,
}) => {
  const [{ canDrop, isDragging, isOver }, drop] = useDrop({
    accept: [DraggableType, "bid", "job", "purchaseOrder"],
    canDrop: (item) => {
      switch (item.type) {
        case "bid":
          // Bids cannot be scheduled to a crew
          return !crew;
        case "event":
          const { event } = item;
          if (event.bid) {
            // Bids cannot be scheduled to a crew
            return !crew;
          } else {
            return (
              !crew ||
              !event.office ||
              crew.offices.some((o) => o.id === event.office.id)
            );
          }
        case "job":
          // Job office must match crew's office
          const { job } = item;
          return (
            !crew ||
            !job.office ||
            crew.offices.some((o) => o.id === job.office.id)
          );
        case "purchaseOrder":
          // Purchase order office must match crew's office
          const { purchaseOrder } = item;
          return (
            !crew ||
            !purchaseOrder.office ||
            crew.offices.some((o) => o.id === purchaseOrder.office.id)
          );
        default:
          break;
      }
      return true;
    },
    collect: (monitor) => ({
      canDrop: !!monitor.canDrop(),
      isDragging: !!monitor.getItem(),
      isOver: !!monitor.isOver(),
    }),
    drop: (item) => {
      switch (item.type) {
        case "bid":
          const { bidId } = item;
          onScheduleBid && onScheduleBid(bidId, date.format("YYYY-MM-DD"));
          break;
        case "event":
          const { eventId } = item;
          onReschedule &&
            onReschedule(eventId, date.format("YYYY-MM-DD"), crew, mode);
          break;
        case "job":
          const { jobId } = item;
          onScheduleJob &&
            onScheduleJob(jobId, crew, date.format("YYYY-MM-DD"));
          break;
        case "purchaseOrder":
          const { purchaseOrderId } = item;
          const isInventoryPO = item.purchaseOrder.isInventoryPO ? true : false;
          onScheduleDelivery &&
            onScheduleDelivery(
              purchaseOrderId,
              date.format("YYYY-MM-DD"),
              crew,
              isInventoryPO
            );
          break;
        case "appointment":
          break;
        default:
          break;
      }
    },
  });

  // console.log(events);
  events = events.filter(function (e) {
    return e.evttype === settings.entity;
  });
  switch (settings.sortoption) {
    case "Date":
      events.sort(function (a, b) {
        // nulls sort after anything else
        if (a.startTime === null) {
          return 1;
        }
        if (b.startTime === null) {
          return -1;
        }

        if (a.startTime < b.startTime) return -1;
        if (a.startTime > b.startTime) return 1;
        else return 0;
      });
      break;
    case "Customer":
      events.sort(function (a, b) {
        if (a.customer && b.customer) {
          return a.customer.name.localeCompare(b.customer.name);
        } else if (a.datasheet && b.datasheet) {
          if (a.datasheet.customer && b.datasheet.customer) {
            return a.datasheet.customer.name.localeCompare(
              b.datasheet.customer.name
            );
          } else {
            return b.id - a.id;
          }
        } else if (a.job && b.job) {
          if (a.job.customer && b.job.customer) {
            return a.job.customer.name.localeCompare(b.job.customer.name);
          } else {
            return b.id - a.id;
          }
        } else if (a.bid && b.bid) {
          if (a.bid.customer && b.bid.customer) {
            return a.bid.customer.name.localeCompare(b.bid.customer.name);
          } else {
            return b.id - a.id;
          }
        } else {
          return b.id - a.id;
        }
      });

      break;
    case "Address":
      events.sort(function (a, b) {
        if (a.datasheet && b.datasheet) {
          if (
            a.datasheet.customer &&
            b.datasheet.customer &&
            a.datasheet.customer.address &&
            b.datasheet.customer.address
          ) {
            const compareState = conververNullToEmpty(
              a.datasheet.customer.address.state
            ).localeCompare(b.datasheet.customer.address.state);
            const compareCity = conververNullToEmpty(
              a.datasheet.customer.address.city
            ).localeCompare(b.datasheet.customer.address.city);
            const compareLine1 = conververNullToEmpty(
              a.datasheet.customer.address.line1
            ).localeCompare(b.datasheet.customer.address.line1);
            const compareLine2 = conververNullToEmpty(
              a.datasheet.customer.address.line2
            ).localeCompare(b.datasheet.customer.address.line2);
            return compareState || compareCity || compareLine1 || compareLine2;
          }
        } else if (a.customer && b.customer) {
          if (
            a.customer &&
            b.customer &&
            a.customer.address &&
            b.customer.address
          ) {
            const compareState = conververNullToEmpty(
              a.customer.address.state
            ).localeCompare(b.customer.address.state);
            const compareCity = conververNullToEmpty(
              a.customer.address.city
            ).localeCompare(b.customer.address.city);
            const compareLine1 = conververNullToEmpty(
              a.customer.address.line1
            ).localeCompare(b.customer.address.line1);
            const compareLine2 = conververNullToEmpty(
              a.customer.address.line2
            ).localeCompare(b.customer.address.line2);
            return compareState || compareCity || compareLine1 || compareLine2;
          }
        } else if (a.job && b.job && a.job.address && b.job.address) {
          const compareState = conververNullToEmpty(
            a.job.address.state
          ).localeCompare(b.job.address.state);
          const compareCity = conververNullToEmpty(
            a.job.address.city
          ).localeCompare(b.job.address.city);
          const compareLine1 = conververNullToEmpty(
            a.job.address.line1
          ).localeCompare(b.job.address.line1);
          const compareLine2 = conververNullToEmpty(
            a.job.address.line2
          ).localeCompare(b.job.address.line2);
          return compareState || compareCity || compareLine1 || compareLine2;
        } else if (a.bid && b.bid && a.bid.address && b.bid.address) {
          const compareState = conververNullToEmpty(
            a.bid.address.state
          ).localeCompare(b.bid.address.state);
          const compareCity = conververNullToEmpty(
            a.bid.address.city
          ).localeCompare(b.bid.address.city);
          const compareLine1 = conververNullToEmpty(
            a.bid.address.line1
          ).localeCompare(b.bid.address.line1);
          const compareLine2 = conververNullToEmpty(
            a.bid.address.line2
          ).localeCompare(b.bid.address.line2);
          return compareState || compareCity || compareLine1 || compareLine2;
        } else {
          return b.id - a.id;
        }
      });
      break;
  }
  function conververNullToEmpty(value) {
    if (value != null) return value;
    else return "";
  }

  return (
    <div
      className={classNames({
        [styles.column]: true,
        [styles.day]: true,
        [styles.active]: active || (canDrop && isOver),
        [styles.disabled]: isDragging && !canDrop,
      })}
      ref={drop}
    >
      {events.map((e) => (
        <ContextMenuTrigger
          collect={contextMenuCollect}
          eventId={e.id}
          jobId={e.job && e.job.id}
          date={date}
          holdToDisplay={1000}
          id={getContextMenuIdForEvent(e)}
          key={e.id}
        >
          <Event
            className={styles.event}
            eventId={e.id}
            event={e}
            jobId={e.job && e.job.id}
            showActions={false}
            styleSelector={makeEventStyle}
          />
        </ContextMenuTrigger>
      ))}
    </div>
  );
};

export default Day;
